import { AxiosError } from 'axios'
import isNil from 'lodash/isNil'
import { NextPage } from 'next'
import getConfig from 'next/config'
import Head from 'next/head'
import styled from 'styled-components'

import Bugsnag from '@bugsnag/js'
import { GetCMSArticle, Section } from 'api/cmsApi'
import { useFetchUserSegmentsQuery } from 'api/consumerSegmentAccessApi'
import Hero from 'homepage/Hero'
import { NavigationSection } from 'homepage/NavigationSection'
import PromotedProductSet from 'homepage/PromotedProductSet'
import SplitScreen from 'homepage/SplitScreen'
import StoresAndHistory from 'homepage/StoresAndHistory'
import { serverSideConfigs, t } from 'localization'
import { logger } from 'server/utils/logger'
import { createMosaicClient } from 'server/utils/mosaicClient'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import CMSSectionType from 'shared/enums/CMSSectionType'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'

const TP = 'pages.index'

type HomePageProps = {
  error?: unknown
  homeFeedData: GetCMSArticle
  staticImages: { [key: string]: string }
}

const HomePage: NextPage<HomePageProps> = ({ error, homeFeedData, staticImages }) => {
  const { country: countryCode } = useShoppingRegionContext()
  const { data: userSegmentData } = useFetchUserSegmentsQuery({
    countryCode,
  })
  const segmentIds = userSegmentData?.userSegmentsList?.map((segment) => segment.id)
  const authorizedFeedSections =
    homeFeedData?.sections?.filter((section: any) => {
      return !section?.segmentId || segmentIds?.includes(section?.segmentId)
    }) || []

  /**
   * Returns the component for each section type in the feed.
   * If the section type is not supported, it will log a warning and return null.
   * @param sections
   */
  const getSectionComponent = (sections?: Section[] | undefined) => {
    if (!!sections?.length) {
      return sections.map((section: Section, i: number) => {
        const { id, type, uuid, ...sectionProps } = section
        switch (type) {
          case CMSSectionType.Hero:
          case CMSSectionType.VideoHero:
            return (
              <Hero
                key={`${uuid || id}`}
                draftId={homeFeedData?.uuid || homeFeedData?.id}
                index={i}
                sectionId={uuid || id}
                sectionType={type}
                {...sectionProps}
              />
            )
          case CMSSectionType.NavigationSection:
            return (
              <NavigationSection
                key={`${uuid || id}`}
                last={i + 1 === sections.length}
                {...sectionProps}
              />
            )
          case CMSSectionType.ProductTemplateCarousel:
            return (
              <PromotedProductSet
                draftId={`${homeFeedData?.uuid || homeFeedData?.id || ''}`}
                index={i + 1}
                key={uuid || id}
                sectionId={`${uuid || id}`}
                {...sectionProps}
              />
            )
          case CMSSectionType.TextAndImage:
            return (
              <SplitScreen
                key={uuid || id}
                {...sectionProps}
                fallbackSrc={staticImages['staff-picks'] || ''}
              />
            )
          case CMSSectionType.CmsSectionType:
            // es-lint-disable-next-line no-console
            console.info(`CMS Section type ${type} is not supported (section id=${uuid || id}).`)
            Bugsnag.notify(
              new Error(`CMS Section type ${type} is not supported (section id=${uuid || id}).`),
              (event) => {
                event.addMetadata('CMSsection', section)
                event.context = 'HomePage'
              },
            )
            return null
        }
      })
    }
  }

  return (
    <>
      <Head>
        <title>{t(`${TP}.pageTitle`, 'Sneakers. Here. | Flight Club')}</title>
      </Head>
      <Sections>
        {!error && getSectionComponent(authorizedFeedSections)}
        <StoresAndHistory data-testid="storesAndHistory" />
      </Sections>
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { req, res, locale } = context
  try {
    const { serverRuntimeConfig } = getConfig()
    const { mosaicRaffleNamespace } = serverRuntimeConfig
    const mosaicClient = createMosaicClient(req, locale)
    const { data } = await mosaicClient.get<GetCMSArticle>('/api/v1/mosaic/articles/fc-home-feed')
    const homeFeedData = {
      ...data,
    }

    const { data: staticImagesData } = await mosaicClient.get(
      `/api/v1/mosaic/${mosaicRaffleNamespace}/static-images`,
    )

    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        homeFeedData,
        staticImages: staticImagesData.images,
      },
    }
  } catch (error) {
    const err = error as Error | AxiosError
    logger.error(error, 'Error rendering home page')

    if (!isNil(res)) {
      res.statusCode = 500
    }

    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        error: { status: 500, message: err.message },
        homeFeedData: { sections: [] },
      },
    }
  }
}

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 3300px;
`

export default HomePage
